import { resolveApiUrl } from '@runtime/shared/api/fetchJSON'
import type { SupportedLocale } from '@runtime/core/i18n/i18n'
import type {
  CoachErrorCode,
  CoachGameContext,
  CoachMessage,
  CoachQuotaScope,
  CoachStreamEvent,
} from './coach.types'

const ERROR_CODES = new Set<CoachErrorCode>([
  'rate_limited',
  'quota_exceeded',
  'timeout',
  'invalid_response',
  'provider_unavailable',
  'not_configured',
  'unknown',
])

export class CoachRequestError extends Error {
  constructor(
    readonly code: CoachErrorCode,
    readonly retryAfterSeconds?: number,
    readonly requestId?: string,
    readonly quotaScope?: CoachQuotaScope,
    readonly resetsAt?: string,
    readonly canLogin = false,
  ) {
    super(`Coach request failed (${code})`)
    this.name = 'CoachRequestError'
  }
}

type AskCoachArgs = {
  question: string
  locale: SupportedLocale
  route: string
  screen: string
  game?: CoachGameContext
  messages: CoachMessage[]
  authToken?: string | null
  posterAccessTokens?: Array<{ themeId: string; accessToken: string }>
}

export async function streamCoach(
  { question, locale, route, screen, game, messages, authToken, posterAccessTokens }: AskCoachArgs,
  onEvent: (event: CoachStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(resolveApiUrl('/api/coach/messages'), {
    method: 'POST',
    signal,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify({
      question,
      locale,
      context: { route, screen, game },
      history: messages
        .filter(message => !message.failed)
        .slice(-8)
        .map(({ role, content }) => ({ role, content })),
      ...(posterAccessTokens?.length ? { posterAccessTokens } : {}),
    }),
  })

  if (!response.ok) {
    let payload: Record<string, unknown> | undefined
    try {
      payload = (await response.json()) as Record<string, unknown>
    } catch {
      payload = undefined
    }
    throw errorFromPayload(payload, response.status)
  }
  if (!response.body) throw new Error('Coach response cannot be streamed')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let completed = false

  try {
    for (;;) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (!line.trim()) continue
        const event = JSON.parse(line) as CoachStreamEvent
        if (event.type === 'error') {
          throw new CoachRequestError(
            event.code,
            event.retryAfterSeconds,
            event.requestId,
            event.quotaScope,
            event.resetsAt,
            event.canLogin,
          )
        }
        onEvent(event)
        if (event.type === 'done') completed = true
      }

      if (done) break
    }

    if (buffer.trim()) {
      const event = JSON.parse(buffer) as CoachStreamEvent
      if (event.type === 'error') {
        throw new CoachRequestError(
          event.code,
          event.retryAfterSeconds,
          event.requestId,
          event.quotaScope,
          event.resetsAt,
          event.canLogin,
        )
      }
      onEvent(event)
      if (event.type === 'done') completed = true
    }
  } finally {
    reader.releaseLock()
  }

  if (!completed) throw new Error('Coach stream ended before completion')
}

function errorFromPayload(payload: Record<string, unknown> | undefined, status: number) {
  const rawCode = payload?.code
  const code =
    typeof rawCode === 'string' && ERROR_CODES.has(rawCode as CoachErrorCode)
      ? (rawCode as CoachErrorCode)
      : status === 429
        ? 'rate_limited'
        : 'unknown'
  return new CoachRequestError(
    code,
    typeof payload?.retryAfterSeconds === 'number' ? payload.retryAfterSeconds : undefined,
    typeof payload?.requestId === 'string' ? payload.requestId : undefined,
    isQuotaScope(payload?.quotaScope) ? payload.quotaScope : undefined,
    typeof payload?.resetsAt === 'string' ? payload.resetsAt : undefined,
    payload?.canLogin === true,
  )
}

function isQuotaScope(value: unknown): value is CoachQuotaScope {
  return ['anonymous', 'account', 'network', 'global'].includes(String(value))
}
